// components/Navbar.js
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  
  const links = [
    { name: 'Home', path: '/' },
    { name: 'Products', path: '/services' },
    { name: 'About', path: '/about' },
    { name: 'Reviews', path: '/reviews' },
    { name: 'Contact', path: '/contact' },
  ];

  // Simple icon renderer function to avoid complex conditional rendering
  const renderIcon = (name) => {
    switch(name) {
      case 'Home':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
        );
      case 'Products':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
        );
      case 'About':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'Reviews':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
          </svg>
        );
      case 'Contact':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
      default:
        return null;
    }
  };

  // Cart icon component for reuse
  const CartIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md py-3 px-4">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <span className="text-xl font-bold text-red-600">3D PrintTech</span>
        </Link>
        
        {/* Desktop Menu */}
        <div className="hidden md:flex items-center space-x-6">
          {links.map((link) => (
            <Link
              key={link.name}
              to={link.path}
              className={`text-base font-medium transition-all duration-300 relative pb-1 ${
                location.pathname === link.path 
                  ? 'text-red-600' 
                  : 'text-gray-800 hover:text-red-600'
              }`}
            >
              <div className="flex items-center">
                {renderIcon(link.name)}
                {link.name}
              </div>
              <span 
                className={`absolute left-0 bottom-0 h-0.5 bg-gradient-to-r from-black to-red-600 transition-all duration-300 ${
                  location.pathname === link.path ? 'w-full' : 'w-0'
                }`}
              ></span>
            </Link>
          ))}
          <Link
            to="/cart"
            className="bg-red-600 hover:bg-red-700 text-white text-base py-2 px-4 rounded-md font-medium transition-colors duration-300 flex items-center"
          >
            <CartIcon />
            View Cart
          </Link>
        </div>
        
        {/* Mobile Menu Button */}
        <button 
          className="md:hidden text-gray-800 focus:outline-none p-1 touch-manipulation"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            {menuOpen ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            )}
          </svg>
        </button>
      </div>
      
      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden fixed top-[53px] left-0 right-0 bg-white shadow-lg border-t border-gray-200 max-h-[calc(100vh-53px)] overflow-y-auto z-40">
          <div className="flex flex-col space-y-4 px-4 py-4">
            {links.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`flex items-center text-base font-medium transition-colors duration-300 py-3 ${
                  location.pathname === link.path 
                    ? 'text-red-600' 
                    : 'text-gray-800 hover:text-red-600'
                }`}
                onClick={() => setMenuOpen(false)}
              >
                {renderIcon(link.name)}
                {link.name}
              </Link>
            ))}
            <Link
              to="/cart"
              className="bg-red-600 hover:bg-red-700 text-white py-2 px-5 rounded-md font-medium transition-colors duration-300 mt-2 w-full sm:w-auto flex items-center justify-center"
              onClick={() => setMenuOpen(false)}
            >
              <CartIcon />
              View Cart
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;